import React, { useState } from 'react';
import { trpc } from '@/utils/trpc-client';
import { PageTitle } from '@/features/shared/page-title';
import { Card, CardContent } from '@/components/forms/ui/card';
import { addDays, addHours, addMonths, addYears, format } from 'date-fns';
import { clsx } from 'clsx';
import { AlertButton, Button, TertiaryButton } from '@/components/display/button';
import { Input } from '@/components/forms/ui/input';
import { Modal } from '@/components/feedback/modal';
import { toast } from 'react-toastify';
import { showErrorToast } from '@/components/feedback/error-toast';
import { ApiKeyCreatedModal } from './api-key-created-modal';
import {
  Calendar,
  Check,
  ChevronDown,
  Clock,
  Copy,
  ExternalLink,
  FileText,
  Infinity as InfinityIcon,
  Key,
  Plus,
  Terminal
} from 'lucide-react';

const toastProps = {
  autoClose: 3000,
  closeOnClick: true,
  style: { width: 'auto', padding: '10px 20px' }
};

type ExpirationOption = 'never' | '1hour' | '1day' | '1month' | '1year' | '2years';

const expirationOptions: { value: ExpirationOption; label: string }[] = [
  { value: 'never', label: 'No expiration' },
  { value: '1hour', label: '1 hour' },
  { value: '1day', label: '1 day' },
  { value: '1month', label: '1 month' },
  { value: '1year', label: '1 year' },
  { value: '2years', label: '2 years' }
];

function CodeBlock({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Ignore copy errors
    }
  };

  return (
    <div className="group relative">
      <pre className="overflow-x-auto rounded-md bg-gray-900 p-3 text-sm text-gray-100">
        <code>{code}</code>
      </pre>
      <button
        onClick={handleCopy}
        className="absolute right-2 top-2 rounded p-1.5 text-gray-400 opacity-0 transition-opacity hover:bg-gray-700 hover:text-gray-200 group-hover:opacity-100"
        title="Copy to clipboard">
        {copied ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  );
}

function UsageExamples() {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="mt-6">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center gap-2 rounded-lg border bg-gray-50 px-4 py-3 text-left text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100">
        <Terminal className="h-4 w-4" />
        <span className="flex-1">How to use your API key</span>
        <ChevronDown
          className={clsx('h-4 w-4 text-gray-400 transition-transform', isOpen && 'rotate-180')}
        />
      </button>

      {isOpen && (
        <div className="mt-3 space-y-4">
          <a
            href="/api/v1/docs/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700 transition-colors hover:bg-blue-100">
            <FileText className="h-4 w-4" />
            <span className="flex-1">View full API documentation (OpenAPI)</span>
            <ExternalLink className="h-4 w-4" />
          </a>

          <div className="rounded-lg border bg-gray-50 p-4">
            <div className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-700">
              Authentication
            </div>
            <p className="mb-3 text-sm text-muted-foreground">
              Include your API key using either header format:
            </p>
            <div className="space-y-2">
              <CodeBlock code="Authorization: ApiKey YOUR_API_KEY" />
              <p className="text-center text-xs text-muted-foreground">or</p>
              <CodeBlock code="x-api-key: YOUR_API_KEY" />
            </div>
          </div>

          <div className="rounded-lg border bg-gray-50 p-4">
            <div className="mb-2 text-sm font-medium text-gray-700">Example: Get Cube Schema</div>
            <CodeBlock
              code={`curl -X GET '${window.location.origin}/api/v1/cube/meta' \\
  -H 'Authorization: ApiKey YOUR_API_KEY'`}
            />
          </div>

          <div className="rounded-lg border bg-gray-50 p-4">
            <div className="mb-2 text-sm font-medium text-gray-700">Example: Query Metrics</div>
            <CodeBlock
              code={`curl -X POST '${window.location.origin}/api/v1/cube/load' \\
  -H 'Authorization: ApiKey YOUR_API_KEY' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "query": {
      "measures": ["line_item_metrics_daily.actual_spend"],
      "timeDimensions": [{
        "dimension": "line_item_metrics_daily.day",
        "dateRange": "Last 7 days"
      }]
    }
  }'`}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function getExpirationDate(option: ExpirationOption): Date | undefined {
  const now = new Date();
  switch (option) {
    case '1hour':
      return addHours(now, 1);
    case '1day':
      return addDays(now, 1);
    case '1month':
      return addMonths(now, 1);
    case '1year':
      return addYears(now, 1);
    case '2years':
      return addYears(now, 2);
    default:
      return undefined;
  }
}

export function ApiKeysAdmin() {
  const utils = trpc.useUtils();
  const { data: keys, isPending, isError, error } = trpc.listApiKeys.useQuery();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [nameError, setNameError] = useState('');
  const [expiration, setExpiration] = useState<ExpirationOption>('never');

  const [createdKey, setCreatedKey] = useState<string | null>(null);

  const [disableConfirm, setDisableConfirm] = useState<{ id: string; name: string } | null>(null);

  const createMutation = trpc.createApiKey.useMutation({
    onSuccess: data => {
      setCreatedKey(data.key);
      setShowCreateForm(false);
      setKeyName('');
      setNameError('');
      setExpiration('never');
      utils.listApiKeys.invalidate();
    },
    onError: () => showErrorToast()
  });

  const disableMutation = trpc.disableApiKey.useMutation({
    onSuccess: () => {
      toast.success('API key disabled', toastProps);
      setDisableConfirm(null);
      utils.listApiKeys.invalidate();
    },
    onError: () => {
      showErrorToast();
      setDisableConfirm(null);
    }
  });

  const handleCreate = () => {
    if (!keyName.trim()) {
      setNameError('Name is required');
      return;
    }
    setNameError('');
    const expirationDate = getExpirationDate(expiration);
    createMutation.mutate({
      name: keyName.trim(),
      expiresAt: expirationDate?.toISOString()
    });
  };

  const handleDisable = () => {
    if (disableConfirm) {
      disableMutation.mutate({ id: disableConfirm.id });
    }
  };

  if (isPending) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <span className="text-muted-foreground">Loading...</span>
      </div>
    );
  }

  if (isError && !keys) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-2">
        <span className="text-red-600">Failed to load API keys</span>
        <span className="text-sm text-muted-foreground">{error.message}</span>
      </div>
    );
  }

  return (
    <div className="p-6">
      <PageTitle title="API Keys" />
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h1 className="mb-2 text-2xl font-bold">API Keys</h1>
          <p className="text-muted-foreground">
            Manage API keys for programmatic access to the API. Keys are shown only once when
            created.
          </p>
        </div>
        <Button onClick={() => setShowCreateForm(true)}>
          <Plus className="mr-1.5 h-4 w-4" />
          Create API Key
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          {keys && keys.length > 0 ? (
            <table className="w-full">
              <thead>
                <tr className="border-b text-left text-sm text-muted-foreground">
                  <th className="pb-2">Key ID</th>
                  <th className="pb-2">Name</th>
                  <th className="pb-2">Created</th>
                  <th className="pb-2">Last Used</th>
                  <th className="pb-2">Expires</th>
                  <th className="pb-2">Status</th>
                  <th className="pb-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {keys.map(key => (
                  <tr
                    key={key.id}
                    className="border-b last:border-0 transition-colors hover:bg-muted/50">
                    <td className="py-3 font-mono text-sm">{key.keyId}</td>
                    <td className="py-3 font-medium">{key.name}</td>
                    <td className="py-3 text-sm">{format(key.createdAt, 'MMM d, yyyy')}</td>
                    <td className="py-3 text-sm">
                      {key.lastUsedAt ? format(key.lastUsedAt, 'MMM d, yyyy') : 'Never'}
                    </td>
                    <td className="py-3 text-sm">
                      {key.expiresAt ? format(key.expiresAt, 'MMM d, yyyy') : 'Never'}
                    </td>
                    <td className="py-3">
                      <span
                        className={clsx(
                          'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium',
                          key.isActive ? 'bg-lime-200 text-lime-800' : 'bg-gray-200 text-gray-600'
                        )}>
                        <span
                          className={clsx(
                            'h-2 w-2 rounded-full',
                            key.isActive ? 'bg-lime-500' : 'bg-gray-400'
                          )}
                        />
                        {key.isActive ? 'Active' : 'Disabled'}
                      </span>
                    </td>
                    <td className="py-3">
                      {key.isActive && (
                        <button
                          className="text-sm text-red-600 hover:text-red-800"
                          onClick={() => setDisableConfirm({ id: key.id, name: key.name })}>
                          Disable
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Key className="mb-4 h-12 w-12 text-gray-300" />
              <p className="text-muted-foreground">No API keys yet</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Create an API key to get started with programmatic access.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Usage Examples - Collapsible */}
      <UsageExamples />

      {/* Create API Key Modal */}
      <Modal
        open={showCreateForm}
        setOpen={setShowCreateForm}
        header={
          <div className="flex items-center gap-3 border-b px-6 py-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
              <Key className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold">Create API Key</h2>
              <p className="text-sm text-muted-foreground">Generate a new key for API access</p>
            </div>
          </div>
        }
        content={
          <div className="space-y-5 px-6 py-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium">Name</label>
              <Input
                type="text"
                placeholder="e.g., CI/CD Pipeline, Analytics Dashboard"
                value={keyName}
                onChange={e => {
                  setKeyName(e.target.value);
                  if (nameError) setNameError('');
                }}
                autoFocus
                className={
                  nameError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                }
              />
              {nameError && <p className="mt-1.5 text-sm text-red-600">{nameError}</p>}
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium">Expiration</label>
              <div className="grid grid-cols-3 gap-2">
                {expirationOptions.map(option => {
                  const isSelected = expiration === option.value;
                  const expiryDate = getExpirationDate(option.value);
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setExpiration(option.value)}
                      className={clsx(
                        'flex flex-col items-center rounded-lg border-2 px-3 py-2.5 text-sm transition-all',
                        isSelected
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      )}>
                      {option.value === 'never' ? (
                        <InfinityIcon className="mb-1 h-4 w-4" />
                      ) : option.value === '1hour' ? (
                        <Clock className="mb-1 h-4 w-4" />
                      ) : (
                        <Calendar className="mb-1 h-4 w-4" />
                      )}
                      <span className="font-medium">{option.label}</span>
                      {expiryDate && (
                        <span className="mt-0.5 text-xs text-muted-foreground">
                          {option.value === '1hour'
                            ? format(expiryDate, 'h:mm a')
                            : format(expiryDate, 'MMM d, yyyy')}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        }
        footer={
          <div className="flex gap-4 border-t px-6 py-4">
            <TertiaryButton
              className="flex-1 justify-center"
              onClick={() => {
                setShowCreateForm(false);
                setKeyName('');
                setNameError('');
                setExpiration('never');
              }}>
              Cancel
            </TertiaryButton>
            <Button
              className="flex-1 justify-center"
              onClick={handleCreate}
              disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Creating...' : 'Create Key'}
            </Button>
          </div>
        }
      />

      {/* API Key Created Modal */}
      <ApiKeyCreatedModal apiKey={createdKey} onClose={() => setCreatedKey(null)} />

      {/* Disable Confirmation Modal */}
      <Modal
        open={!!disableConfirm}
        setOpen={open => {
          if (!open) setDisableConfirm(null);
        }}
        header={
          <div className="border-b px-6 py-4">
            <h2 className="text-xl font-semibold text-red-600">Disable API Key</h2>
          </div>
        }
        content={
          <div className="px-6 py-4">
            <p>
              Are you sure you want to disable the API key{' '}
              <span className="font-semibold">{disableConfirm?.name}</span>?
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              This action cannot be undone. Any applications using this key will lose access.
            </p>
          </div>
        }
        footer={
          <div className="flex gap-4 border-t px-6 py-4">
            <TertiaryButton
              className="flex-1 justify-center"
              onClick={() => setDisableConfirm(null)}>
              Cancel
            </TertiaryButton>
            <AlertButton
              className="flex-1 justify-center"
              onClick={handleDisable}
              disabled={disableMutation.isPending}>
              {disableMutation.isPending ? 'Disabling...' : 'Disable Key'}
            </AlertButton>
          </div>
        }
      />
    </div>
  );
}
